type Problem<T extends string> = `❌ ${T}`;

type Equals<X, Y> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? true : false;

type DeepValidate<Base, Target> = {
  [K in keyof Base | keyof Target]: K extends keyof Base
    ? K extends keyof Target
      ? Base[K] extends (...args: infer A) => string
        ? Target[K] extends (...args: infer B) => string
          ? Equals<A, B> extends true
            ? Target[K]
            : Problem<"Parameter mismatch">
          : Problem<"Expected function">
        : Base[K] extends object
          ? Target[K] extends object
            ? DeepValidate<Base[K], Target[K]>
            : Problem<"Expected object">
          : Target[K] extends string | number | boolean
            ? Target[K]
            : Problem<"Expected primitive value">
      : Problem<"Missing key">
    : Problem<"Extra key">;
};

type I18nConfig<T, BaseKey extends keyof T> = {
  [K in keyof T]: K extends BaseKey ? T[K] : DeepValidate<T[BaseKey], T[K]>;
};

const createI18n = <
  const T extends Record<string, object> = Record<string, object>,
  BaseKey extends keyof T = "en",
>(
  translations: I18nConfig<T, BaseKey>,
): T => translations as T;

const translations = createI18n({
  en: {
    nav: {
      record: "Record",
      bets: "Bets",
      organize: "Organize",
      admin: "Admin",
      tv: "TV",
      demo: "Demo",
    },
    app: {
      title: "🏆 Trip Competition",
    },
    match: {
      title: "Record Result",
      setup: "Set Up Match",
      whatPlayed: "What did you play?",
      ffaInfo: (n: number) => `Free-for-all: ${n} players competing individually.`,
      randomize: "🎲 Randomize Teams",
      recordResults: "▶️ Record Results",
      tapOrder: "Tap players/teams in finishing order (1st place first).",
      doubleTapTie: "Double-tap for a tie with previous.",
      tapUndo: "Tap a ranked item to undo.",
      submit: "✅ Submit Result",
      back: "← Back",
      needPlayers: "Add at least 2 players in the Organizer first.",
      needActivities: "Add at least one activity in the Organizer first.",
      tied: "(tied)",
      win: "Win",
      ready: "Ready",
      draw: "Draw",
      tournamentComplete: "Tournament complete. Submit the final standings when ready.",
      tournamentWaiting: "Waiting for the next match to become ready...",
      pts: "pts",
    },
    leaderboard: {
      title: "🏆 Leaderboard",
      rank: "#",
      name: "Player",
      points: "Points",
      games: "Games",
      streak: "Streak",
    },
    bet: {
      title: "🎰 Place Your Bet",
      whoBetting: "Who's betting?",
      whichActivity: "Betting on which activity?",
      whoWins: "Who wins?",
      howMuch: "How much?",
      bankroll: "Bankroll",
      potentialPayout: "Potential payout",
      atOdds: (odds: string) => `at ${odds}x odds`,
      placeBet: "🎰 Place Bet",
      pendingWarning: "⚠️ You already have an unresolved bet on this activity.",
      todayTitle: "📋 Today's Bets",
      leaderboardTitle: "🧠 Best Gamblers",
      needPlayers: "Need at least 2 players to start betting.",
      bet: "bet",
      on: "on",
      in: "in",
      won: (n: number) => `✓ Won ${n}💰`,
      lost: (n: number) => `✗ Lost ${n}💰`,
      record: "Record",
      net: "Net",
    },
    activity: {
      title: "🎯 Activities",
      addPlaceholder: "Activity name (e.g. Bowling)",
      add: "Add Activity",
      variance: "Variance",
      format: "Format",
      weight: "Weight",
      participants: "Participants (empty = everyone)",
      numTeams: "Number of teams",
      teamSize: "Team size (0 = auto)",
      randomize: "Randomize team assignment",
      save: "Save",
      cancel: "Cancel",
      deleteConfirm: (name: string) => `Delete activity "${name}"?`,
      varianceLow: "Low (skill-dominant)",
      varianceMedium: "Medium (balanced)",
      varianceHigh: "High (anything can happen)",
      formatFfa: "Free-for-all",
      formatTeams: "Teams",
      formatTournament: "Tournament",
      weightLow: "Low (0.5×)",
      weightNormal: "Normal (1×)",
      weightHigh: "High (1.5×)",
      weightDouble: "Double (2×)",
      teams: (n: number) => `${n} teams`,
      random: "(random)",
      manual: "(manual)",
      topK: "Places that earn points (0 = all)",
      tuning: "Tuning",
      selectAll: "All in",
      deselectAll: "Clear",
      confirmDelete: (name: string) => `Remove "${name}"?`,
      tournamentFormat: "Tournament format",
      tournamentFormatSingle: "Single Elimination",
      tournamentFormatSingleDesc:
        "Winners advance, losers are out. Quick and simple, but one bad match can be fatal.",
      tournamentFormatDouble: "Double Elimination",
      tournamentFormatDoubleDesc:
        "Players get a second chance after their first loss. More matches and opportunities for redemption, but takes longer.",
      tournamentFormatRoundRobin: "Round Robin",
      tournamentFormatRoundRobinDesc:
        "Everyone plays against everyone. Very fair and thorough, but can take a lot of time with many players.",
      tournamentFormatSwiss: "Swiss System",
      tournamentFormatSwissDesc:
        "Players play a set number of rounds against opponents with similar records. No elimination, more balanced matchups.",
    },
    player: {
      title: "👥 Players",
      addPlaceholder: "Player name",
      add: "Add Player",
      deleteConfirm: (name: string) => `Delete "${name}"?`,
      required: "Player name is required.",
      alreadyExists: (name: string) => `"${name}" already exists.`,
    },
    tournament: {
      title: "🏟️ Tournament Mode",
      tournaments: "Tournaments",
      new: "+ New Tournament",
      namePlaceholder: "Tournament name",
      format: "Format",
      activity: "Activity",
      teamSize: "Team size",
      selectPlayers: (n: number) => `Select players (${n} selected)`,
      create: "🏟️ Create Tournament",
      none: "No tournaments yet. Create one!",
      complete: "🏆 Complete",
      inProgress: "⏳ In Progress",
      setup: "Setup",
      wins: "wins the tournament!",
      standings: "Standings",
      pending: "Pending Matches",
      nextSwiss: "➡️ Next Swiss Round",
      finish: "🏁 Finish Tournament",
      deleteConfirm: "Delete this tournament?",
      startTournament: "🏟️ Start Tournament",
      bracketReady: "Bracket is ready! Record each match result below.",
      tournamentComplete: "Tournament complete!",
      noTournament: "Select participants and start the tournament.",
      round: (n: number) => `Round ${n}`,
      swissRoundsPlayed: (n: number, total: number) => `${n} of ${total} rounds played`,
    },
    multiplier: {
      title: "🎚️ Scoring Multipliers",
      active: (n: number, total: number) => `(${n}/${total} active)`,
      description:
        "Toggle multipliers on/off and tune parameters. Changes apply to all future matches.",
      parameters: "Parameters",
      scale: "Base",
      scaleDesc: "Base multiplier to keep points in a reasonable range.",
      weight: "Activity Weight",
      weightDesc: "Scale points by activity importance",
      upset: "Upset Bonus",
      upsetDesc: "Bonus for winning when expected to lose",
      streak: "Win Streak",
      streakDesc: "Bonus for consecutive wins",
      fatigue: "Fatigue",
      fatigueDesc: "Diminishing returns after many games",
      momentum: "Momentum",
      momentumDesc: "Smooth bonus for hot streaks",
      comeback: "Comeback",
      comebackDesc: "Bonus for winning when far behind",
      consistency: "Consistency",
      consistencyDesc: "Bonus for stable skill ratings",
      closeness: "Close Match",
      closenessDesc: "Bonus for tight, competitive games",
      variety: "Variety",
      varietyDesc: "Bonus for playing different activities",
      rivalry: "Rivalry",
      rivalryDesc: "Bonus for beating someone who beat you",
      recalculate: "🔄 Recalculate All Scores",
      recalculateConfirm:
        "Recalculate all scores with current multipliers? This replays every match from scratch.",
      recalculated: (n: number) => `✅ Recalculated ${n} match${n === 1 ? "" : "es"} successfully.`,
      exportConfig: "📤 Export Config",
      importConfig: "📥 Import Config",
      resetDefaults: "↩️ Reset Defaults",
      invalidFile: "Invalid config file.",
    },
    hint: {
      scaleFactor:
        "Adjusts the base points awarded for matches. Increase if points feel too low, decrease if too high.",
      fatigueHalfLife:
        "After this many games, fatigue halves your bonus. Low = fast decay, high = slow.",
      momentumFactor: "Bonus per consecutive win. 0.05 = +5% per win in streak.",
      momentumCap: "Maximum total momentum bonus. 0.3 = cap at +30%.",
      comebackThreshold:
        "Rating deficit needed before comeback triggers. Lower = triggers more often.",
      comebackMaxBonus: "Maximum comeback bonus. 0.5 = up to +50% when far behind.",
      consistencySigmaThreshold:
        "Uncertainty (σ) must be below this. Lower = stricter, only rewards well-established players.",
      consistencyBonus: "Maximum bonus for consistent players. 0.15 = up to +15%.",
      closenessMaxDelta:
        "Maximum rating change (Δμ) to count as 'close'. Smaller = only very tight matches.",
      closenessBonus: "Maximum bonus for close matches. 0.2 = up to +20%.",
      varietyDecay:
        "How fast the variety bonus decays after repeating an activity. Lower = forgives faster.",
      varietyBonus: "Maximum bonus for playing a new activity. 0.2 = up to +20%.",
      rivalryLookback: "How many recent matches to search for past defeats. More = longer memory.",
      rivalryBonus: "Bonus when beating your recent rival. 0.25 = up to +25%.",
    },
    data: {
      export: "📤 Export",
      import: "📥 Import",
      reset: "🗑 Reset",
      resetConfirm: "Reset all data? This cannot be undone.",
      resetScores: "🔄 Reset Scores",
      resetScoresConfirm:
        "Reset all scores, matches and bets? Players and activities will be kept.",
      importError: "Invalid backup file.",
    },
    history: {
      title: "📜 Recent Results",
      deleteConfirm: "Delete this match result?",
      unknownActivity: "Unknown Activity",
      unknownTime: "Unknown time",
      pts: "pts",
      breakdown: "Points breakdown",
      base: "Base",
    },
    tv: {
      title: "🏆 Trip Scoreboard",
      noPlayers: "No players yet",
      waitingForPlayers: "Waiting for players...",
      noMatchesYet: "No matches played yet",
      matchesPlayed: (n: number) => `${n} match${n === 1 ? "" : "es"} played`,
    },
    admin: {
      matchmaking: "🎯 Smart Matchmaking",
      matchmakingTeams: "Teams:",
      matchmakingTeamSize: "Team size:",
      matchmakingSuggest: "💡 Suggest Next Match",
      suggested: (name: string) => `📋 Suggested: ${name}`,
      balance: "Balance",
      skill: "Skill",
    },
    arena: {
      railGame: "Activity",
      railPodium: "Podium",
      railRecap: "Recap",
      railLineup: "Lineup",
      railBracket: "Bracket",
      matchCount: (n: number) => `${n} activit${n === 1 ? "y" : "ies"}`,
      changeResult: "Change result",
      back: "Back",
      recordResults: "Record results →",
      recordResult: "Record result",
      confirmSave: "Confirm",
      crownChampion: "Crown the champion",
      finishTournament: "Finish tournament",
      stagePickGame: "Stage 1 · Pick an activity",
      whatPlaying: "What are we doing?",
      needPlayersArena: "Add at least 2 players in the organizer first.",
      needActivitiesArena: "No activities yet. Add one in the organizer first.",
      tapToStart: "Tap an activity to start building your lineup.",
      formatFfa: "Free for all",
      formatTeams: "Teams",
      formatTournament: "Tournament",
      stageLineup: "Stage 2 · Lineup",
      lineupSubtitle: "Drag players into teams, or shuffle for chaos.",
      shuffle: "Shuffle",
      bench: "Bench",
      dropHere: "Drop here",
      dragHere: "Drag players here",
      everyonePlaying: "Everyone's playing.",
      stagePodium: (n: number) => `Stage ${n} · Podium`,
      whoTookIt: "Who took it?",
      podiumSubtitle: "Tap to rank in order. Double-tap to tie. Tap a ranked entry to undo.",
      tied: "(tied)",
      recap: "Recap",
      itsATie: "It's a tie.",
      weHaveAWinner: "We have a winner.",
      coChampions: "Co-champions",
      champion: "Champion",
      pts: "pts",
      stageTournament: "Stage 2 · Tournament",
      standingsPlayer: "Player",
      standingsMP: "MP",
      standingsW: "W",
      standingsD: "D",
      standingsL: "L",
      standingsPts: "Pts",
      showLess: "Show less",
      showAll: (n: number) => `Show all ${n} players`,
      roundN: (n: number) => `Round ${n}`,
      championship: "Championship",
      bracketN: (n: number) => `Bracket ${n}`,
      ready: "Ready",
      bye: "BYE",
      tbd: "TBD",
      draw: "Draw",
      tournamentCompleteMsg: "Tournament complete. Submit the final standings.",
      waitingNextMatch: "Waiting for the next activity…",
      leaveRoom: "Leave room",
    },
    room: {
      subtitle: "Enter your room code to join a session",
      join: "Join Room",
      placeholder: "e.g. bob",
    },
    general: {
      brandName: "The Fervent Standings",
      vs: "vs",
      everyone: "Everyone",
      teamN: (n: number) => `Team ${n}`,
      unassigned: "Unassigned Players (drag to assign)",
      dropHere: "Drop players here",
      alreadyExists: (name: string) => `"${name}" already exists.`,
      required: "Field is required.",
      players: (n: number) => `${n} player${n === 1 ? "" : "s"}`,
    },
  },

  fr: {
    nav: {
      record: "Résultat",
      bets: "Paris",
      organize: "Organiser",
      admin: "Admin",
      tv: "TV",
      demo: "Démo",
    },
    app: {
      title: "🏆 Compétition du Trip",
    },
    match: {
      title: "Encoder le résultat",
      setup: "Préparer le match",
      whatPlayed: "Vous avez joué à quoi ?",
      ffaInfo: (n: number) => `Chacun pour soi : ${n} joueurs en individuel.`,
      randomize: "🎲 Équipes au hasard",
      recordResults: "▶️ Encoder les résultats",
      tapOrder: "Appuyez sur les joueurs/équipes dans l’ordre d’arrivée (le 1er d’abord).",
      doubleTapTie: "Double-tap pour une égalité avec le précédent.",
      tapUndo: "Appuyez sur un élément pour annuler.",
      submit: "✅ Valider le résultat",
      back: "← Retour",
      needPlayers: "Ajoutez d’abord au moins 2 joueurs dans l’onglet Organiser.",
      needActivities: "Ajoutez d’abord au moins une activité dans l’onglet Organiser.",
      tied: "(égalité)",
      win: "Victoire",
      ready: "Prêt",
      draw: "Match nul",
      tournamentComplete: "Tournoi terminé. Validez le classement final.",
      tournamentWaiting: "En attente que le prochain match soit prêt…",
      pts: "pts",
    },
    leaderboard: {
      title: "🏆 Classement",
      rank: "#",
      name: "Joueur",
      points: "Points",
      games: "Matchs",
      streak: "Série",
    },
    bet: {
      title: "🎰 Placer un pari",
      whoBetting: "Qui parie ?",
      whichActivity: "Sur quelle activité ?",
      whoWins: "Qui gagne ?",
      howMuch: "Mise ?",
      bankroll: "Solde",
      potentialPayout: "Gain potentiel",
      atOdds: (odds: string) => `à ${odds}x la cote`,
      placeBet: "🎰 Placer le pari",
      pendingWarning: "⚠️ Il y a déjà un pari en cours sur cette activité.",
      todayTitle: "📋 Paris du jour",
      leaderboardTitle: "🧠 Meilleurs parieurs",
      needPlayers: "Il faut au moins 2 joueurs pour parier.",
      bet: "parie",
      on: "sur",
      in: "dans",
      won: (n: number) => `✓ Gagné ${n}💰`,
      lost: (n: number) => `✗ Perdu ${n}💰`,
      record: "Bilan",
      net: "Net",
    },
    activity: {
      title: "🎯 Activités",
      addPlaceholder: "Nom de l’activité (ex: Bowling)",
      add: "Ajouter une activité",
      variance: "Variance",
      format: "Format",
      weight: "Poids",
      participants: "Participants (vide = tout le monde)",
      numTeams: "Nombre d’équipes",
      teamSize: "Taille des équipes (0 = auto)",
      randomize: "Répartition au hasard",
      save: "Enregistrer",
      cancel: "Annuler",
      deleteConfirm: (name: string) => `Supprimer l’activité "${name}" ?`,
      varianceLow: "Faible (compétence dominante)",
      varianceMedium: "Moyenne (équilibré)",
      varianceHigh: "Élevée (tout peut arriver)",
      formatFfa: "Chacun pour soi",
      formatTeams: "Équipes",
      formatTournament: "Tournoi",
      weightLow: "Faible (0.5×)",
      weightNormal: "Normal (1×)",
      weightHigh: "Élevé (1.5×)",
      weightDouble: "Double (2×)",
      teams: (n: number) => `${n} équipes`,
      random: "(au hasard)",
      manual: "(manuel)",
      topK: "Places qui rapportent des points (0 = toutes)",
      tuning: "Réglages",
      selectAll: "Tous",
      deselectAll: "Aucun",
      confirmDelete: (name: string) => `Supprimer "${name}" ?`,
      tournamentFormat: "Format du tournoi",
      tournamentFormatSingle: "Élimination simple",
      tournamentFormatSingleDesc:
        "Les gagnants passent, les perdants sont éliminés. Rapide et simple, mais un mauvais match peut tout changer.",
      tournamentFormatDouble: "Double élimination",
      tournamentFormatDoubleDesc:
        "Les joueurs ont une deuxième chance après une première défaite. Plus long, mais plus juste.",
      tournamentFormatRoundRobin: "Toutes rondes",
      tournamentFormatRoundRobinDesc:
        "Tout le monde joue contre tout le monde. Très équitable, mais peut prendre du temps.",
      tournamentFormatSwiss: "Système suisse",
      tournamentFormatSwissDesc:
        "Matchs contre des joueurs au niveau similaire. Pas d’élimination, plus équilibré.",
    },
    player: {
      title: "👥 Joueurs",
      addPlaceholder: "Nom du joueur",
      add: "Ajouter un joueur",
      deleteConfirm: (name: string) => `Supprimer "${name}" ?`,
      required: "Le nom du joueur est obligatoire.",
      alreadyExists: (name: string) => `"${name}" existe déjà.`,
    },
    tournament: {
      title: "🏟️ Mode tournoi",
      tournaments: "Tournois",
      new: "+ Nouveau tournoi",
      namePlaceholder: "Nom du tournoi",
      format: "Format",
      activity: "Activité",
      teamSize: "Taille des équipes",
      selectPlayers: (n: number) => `Sélectionner les joueurs (${n} sélectionnés)`,
      create: "🏟️ Créer le tournoi",
      none: "Pas encore de tournoi. Lancez-vous !",
      complete: "🏆 Terminé",
      inProgress: "⏳ En cours",
      setup: "Configuration",
      wins: "remporte le tournoi !",
      standings: "Classement",
      pending: "Matchs à jouer",
      nextSwiss: "➡️ Ronde suisse suivante",
      finish: "🏁 Terminer le tournoi",
      deleteConfirm: "Supprimer ce tournoi ?",
      startTournament: "🏟️ Démarrer le tournoi",
      bracketReady: "Le tableau est prêt ! Encodez les résultats ci-dessous.",
      tournamentComplete: "Tournoi terminé !",
      noTournament: "Sélectionnez les joueurs et lancez le tournoi.",
      round: (n: number) => `Ronde ${n}`,
      swissRoundsPlayed: (n: number, total: number) => `${n} sur ${total} rondes jouées`,
    },
    multiplier: {
      title: "🎚️ Multiplicateurs",
      active: (n: number, total: number) => `(${n}/${total} actifs)`,
      description:
        "Activez ou désactivez les multiplicateurs et ajustez les paramètres. Ça s’applique aux prochains matchs.",
      parameters: "Paramètres",
      scale: "Base",
      scaleDesc: "Multiplicateur de base pour garder des points cohérents.",
      weight: "Poids de l’activité",
      weightDesc: "Ajuste les points selon l’importance",
      upset: "Bonus surprise",
      upsetDesc: "Bonus si on gagne en étant outsider",
      streak: "Série",
      streakDesc: "Bonus pour des victoires d’affilée",
      fatigue: "Fatigue",
      fatigueDesc: "Moins de gain après beaucoup de matchs",
      momentum: "Momentum",
      momentumDesc: "Bonus progressif sur une bonne série",
      comeback: "Remontée",
      comebackDesc: "Bonus quand on revient de loin",
      consistency: "Régularité",
      consistencyDesc: "Bonus pour des performances stables",
      closeness: "Match serré",
      closenessDesc: "Bonus pour des matchs disputés",
      variety: "Variété",
      varietyDesc: "Bonus pour changer d’activité",
      rivalry: "Rivalité",
      rivalryDesc: "Bonus pour battre quelqu’un qui vous a battu",
      recalculate: "🔄 Recalculer les scores",
      recalculateConfirm:
        "Recalculer tous les scores avec les paramètres actuels ? Tous les matchs seront rejoués.",
      recalculated: (n: number) =>
        `✅ ${n} match${n === 1 ? "" : "s"} recalculé${n === 1 ? "" : "s"}.`,
      exportConfig: "📤 Exporter la config",
      importConfig: "📥 Importer la config",
      resetDefaults: "↩️ Réinitialiser",
      invalidFile: "Fichier invalide.",
    },
    hint: {
      scaleFactor:
        "Ajuste les points de base. Augmentez si c’est trop bas, diminuez si c’est trop élevé.",
      fatigueHalfLife:
        "Après ce nombre de matchs, la fatigue divise le bonus par deux. Bas = baisse rapide.",
      momentumFactor: "Bonus par victoire d’affilée. 0.05 = +5% par victoire.",
      momentumCap: "Bonus momentum max. 0.3 = max +30%.",
      comebackThreshold: "Retard nécessaire pour activer la remontée. Plus bas = plus fréquent.",
      comebackMaxBonus: "Bonus remontée max. 0.5 = jusqu’à +50%.",
      consistencySigmaThreshold: "Incertitude (σ) max. Plus bas = plus strict.",
      consistencyBonus: "Bonus max pour régularité. 0.15 = +15%.",
      closenessMaxDelta:
        "Différence max (Δμ) pour un match serré. Plus petit = matchs très serrés seulement.",
      closenessBonus: "Bonus max pour match serré. 0.2 = +20%.",
      varietyDecay: "Vitesse de perte du bonus variété.",
      varietyBonus: "Bonus max pour nouvelle activité. 0.2 = +20%.",
      rivalryLookback: "Nombre de matchs à analyser pour les rivalités.",
      rivalryBonus: "Bonus quand vous battez un rival. 0.25 = +25%.",
    },
    data: {
      export: "📤 Exporter",
      import: "📥 Importer",
      reset: "🗑 Réinitialiser",
      resetConfirm: "Réinitialiser toutes les données ? Action irréversible.",
      resetScores: "🔄 Réinitialiser les scores",
      resetScoresConfirm:
        "Réinitialiser scores, matchs et paris ? Les joueurs et activités restent.",
      importError: "Fichier invalide.",
    },
    history: {
      title: "📜 Résultats récents",
      deleteConfirm: "Supprimer ce résultat ?",
      unknownActivity: "Activité inconnue",
      unknownTime: "Heure inconnue",
      pts: "pts",
      breakdown: "Détail des points",
      base: "Base",
    },
    tv: {
      title: "🏆 Tableau des scores",
      noPlayers: "Pas encore de joueurs",
      waitingForPlayers: "En attente de joueurs...",
      noMatchesYet: "Aucun match joué",
      matchesPlayed: (n: number) => `${n} match${n === 1 ? "" : "s"} joué${n === 1 ? "" : "s"}`,
    },
    admin: {
      matchmaking: "🎯 Matchmaking intelligent",
      matchmakingTeams: "Équipes :",
      matchmakingTeamSize: "Taille des équipes :",
      matchmakingSuggest: "💡 Proposer un match",
      suggested: (name: string) => `📋 Suggestion : ${name}`,
      balance: "Équilibre",
      skill: "Niveau",
    },
    arena: {
      railGame: "Activité",
      railPodium: "Podium",
      railRecap: "Récap",
      railLineup: "Composition",
      railBracket: "Tableau",
      matchCount: (n: number) => `${n} activité${n === 1 ? "" : "s"}`,
      changeResult: "Modifier le résultat",
      back: "Retour",
      recordResults: "Encoder les résultats →",
      recordResult: "Encoder le résultat",
      confirmSave: "Confirmer",
      crownChampion: "Couronner le champion",
      finishTournament: "Terminer le tournoi",
      stagePickGame: "Étape 1 · Choix du activité",
      whatPlaying: "On fait quoi ?",
      needPlayersArena: "Ajoutez d'abord au moins 2 joueurs dans le panneau Organiser.",
      needActivitiesArena: "Pas encore d'activité. Ajoutez-en une dans le panneau Organiser.",
      tapToStart: "Appuyez sur une activité pour composer votre équipe.",
      formatFfa: "Chacun pour soi",
      formatTeams: "Équipes",
      formatTournament: "Tournoi",
      stageLineup: "Étape 2 · Composition",
      lineupSubtitle: "Glissez les joueurs dans les équipes, ou mélangez pour le chaos.",
      shuffle: "Mélanger",
      bench: "Banc",
      dropHere: "Déposez ici",
      dragHere: "Glissez des joueurs ici",
      everyonePlaying: "Tout le monde joue.",
      stagePodium: (n: number) => `Étape ${n} · Podium`,
      whoTookIt: "Qui a gagné ?",
      podiumSubtitle:
        "Appuyez pour classer. Double-tap pour une égalité. Appuyez sur un classé pour annuler.",
      tied: "(égalité)",
      recap: "Récap",
      itsATie: "Égalité.",
      weHaveAWinner: "On a un gagnant.",
      coChampions: "Co-champions",
      champion: "Champion",
      pts: "pts",
      stageTournament: "Étape 2 · Tournoi",
      standingsPlayer: "Joueur",
      standingsMP: "MJ",
      standingsW: "V",
      standingsD: "N",
      standingsL: "D",
      standingsPts: "Pts",
      showLess: "Voir moins",
      showAll: (n: number) => `Voir les ${n} joueurs`,
      roundN: (n: number) => `Ronde ${n}`,
      championship: "Finale",
      bracketN: (n: number) => `Tableau ${n}`,
      ready: "Prêt",
      bye: "BYE",
      tbd: "À déterminer",
      draw: "Match nul",
      tournamentCompleteMsg: "Tournoi terminé. Validez le classement final.",
      waitingNextMatch: "En attente de la prochaine activité…",
      leaveRoom: "Quitter la salle",
    },
    room: {
      subtitle: "Entrez votre code de salle pour rejoindre une session",
      join: "Rejoindre",
      placeholder: "ex. bob",
    },
    general: {
      brandName: "Le Classement Ardent",
      vs: "vs",
      everyone: "Tout le monde",
      teamN: (n: number) => `Équipe ${n}`,
      unassigned: "Joueurs non assignés (glisser pour attribuer)",
      dropHere: "Déposez les joueurs ici",
      alreadyExists: (name: string) => `"${name}" existe déjà.`,
      required: "Champ requis.",
      players: (n: number) => `${n} joueur${n === 1 ? "" : "s"}`,
    },
  },

  vl: {
    nav: {
      record: "Resultaatekke",
      bets: "Pariekes",
      organize: "Organisere",
      admin: "Beheerke",
      tv: "Téléke",
      demo: "Demo-ke",
    },
    app: {
      title: "🏆 Trip Competitie-ke",
    },
    match: {
      title: "Resultaat Ingeven",
      setup: "Matchke Opzetten",
      whatPlayed: "Wa hebt gelle gespeeld?",
      ffaInfo: (n: number) => `Chacun pour soi: ${n} spelers die elk voor hun eigen gaan, hé.`,
      randomize: "🎲 Teams door elkaar gooien",
      recordResults: "▶️ Resultaten Ingeven",
      tapOrder: "Duw op de spelers/teams in volgorde van de uitslag (eerste plek eerst, allez).",
      doubleTapTie: "Dubbeltik voor ex aequo met de vorige.",
      tapUndo: "Tik nog is op een gekozen ding om ’t ongedaan te maken.",
      submit: "✅ Resultaat Opslaan, voilà",
      back: "← Terugke",
      needPlayers: "Zet eerst minstens 2 spelers via ’t tabblad Organisere, hè.",
      needActivities: "Ge moet eerst minstens één activiteitje toevoegen via Organisere.",
      tied: "(gelijk, hein)",
      win: "Gewonnen",
      ready: "Klaar",
      draw: "Gelijkspel",
      tournamentComplete: "Toernooike gedaan. Dien het eindklassement in als ge wilt.",
      tournamentWaiting: "Aan 't wachten tot het volgende matchke klaar is...",
      pts: "ptn",
    },
    leaderboard: {
      title: "🏆 Klassement",
      rank: "#",
      name: "Spelerke",
      points: "Punten",
      games: "Matchkes",
      streak: "Reekske",
    },
    bet: {
      title: "🎰 Parieke Plaatsen",
      whoBetting: "Wie zet er in?",
      whichActivity: "Op welke activiteit, zeg?",
      whoWins: "Wie pakt het, denkt ge?",
      howMuch: "Hoeveel legt ge erop?",
      bankroll: "Caisse",
      potentialPayout: "Mogelijke uitbetaling, amai",
      atOdds: (odds: string) => `aan ${odds}x cote`,
      placeBet: "🎰 Parieke Plaatsen",
      pendingWarning: "⚠️ Ge hebt al een parieke lopen op deze activiteit, hé.",
      todayTitle: "📋 Pariekes van Vandaag",
      leaderboardTitle: "🧠 Slimste Gokkers",
      needPlayers: "Minstens 2 spelers nodig om te kunnen gokken, jong.",
      bet: "zet",
      on: "op",
      in: "in",
      won: (n: number) => `✓ Gewonnen ${n} ballekes 💰`,
      lost: (n: number) => `✗ Verloren ${n} ballekskes 💰`,
      record: "Scoreke",
      net: "Netto-ke",
    },
    activity: {
      title: "🎯 Activiteitjes",
      addPlaceholder: "Naam van de activiteit (bvb. Bowlenke)",
      add: "Activiteit Toevoegen",
      variance: "Variatie",
      format: "Formaatje",
      weight: "Gewichtje",
      participants: "Deelnemers (leeg = tout le monde)",
      numTeams: "Aantal teamskes",
      teamSize: "Grootte van de teams (0 = automatisch, hein)",
      randomize: "Teams willekeurig indelen",
      save: "Opslaan",
      cancel: "Annulere",
      deleteConfirm: (name: string) => `Activiteit "${name}" verwijderen, zijt ge zeker?`,
      varianceLow: "Laag (vaardigheid telt vooral)",
      varianceMedium: "Gemiddeld (beetje van alles)",
      varianceHigh: "Hoog (alles kan, amai)",
      formatFfa: "Iedereen voor zich",
      formatTeams: "Teamskes",
      formatTournament: "Toernooike",
      weightLow: "Laag (0.5×)",
      weightNormal: "Normaal (1×)",
      weightHigh: "Hoog (1.5×)",
      weightDouble: "Dubbel (2×, hé)",
      teams: (n: number) => `${n} teamskes`,
      random: "(willekeurig)",
      manual: "(met de hand, allez)",
      topK: "Plaatsen die punten opleveren (0 = allemaal)",
      tuning: "Afstelling",
      selectAll: "Allemaal erin",
      deselectAll: "Niemand",
      confirmDelete: (name: string) => `"${name}" weg ermee?`,
      tournamentFormat: "Toernooiformaat",
      tournamentFormatSingle: "Enkele Eliminatie",
      tournamentFormatSingleDesc:
        "Winnaars gaan verder, verliezers zijn gedaan. Rap en simpel, maar één slechte match en ’t is gedaan.",
      tournamentFormatDouble: "Dubbele Eliminatie",
      tournamentFormatDoubleDesc:
        "Ge krijgt nog een kans na uw eerste verlies. Meer matchkes en kans om terug te komen, maar duurt langer.",
      tournamentFormatRoundRobin: "Round Robin",
      tournamentFormatRoundRobinDesc:
        "Iedereen tegen iedereen. Heel eerlijk, maar kan lang duren, zeg.",
      tournamentFormatSwiss: "Zwitsers Systeem",
      tournamentFormatSwissDesc:
        "Ge speelt tegen mensen met gelijkaardige resultaten. Geen eliminatie, schoon gebalanceerd.",
    },
    player: {
      title: "👥 Spelerskes",
      addPlaceholder: "Naam van de speler",
      add: "Speler Toevoegen",
      deleteConfirm: (name: string) => `"${name}" verwijderen, serieus?`,
      required: "Spelernaam is verplicht, hé.",
      alreadyExists: (name: string) => `"${name}" bestaat al, jong.`,
    },
    tournament: {
      title: "🏟️ Toernooimodus",
      tournaments: "Toernooikes",
      new: "+ Nieuw Toernooike",
      namePlaceholder: "Naam van ’t toernooi",
      format: "Formaat",
      activity: "Activiteit",
      teamSize: "Teamgrootte",
      selectPlayers: (n: number) => `Selecteer spelers (${n} gekozen, allez)`,
      create: "🏟️ Toernooi Maken",
      none: "Nog geen toernooikes. Maak er ene!",
      complete: "🏆 Afgewerkt",
      inProgress: "⏳ Bezig, hein",
      setup: "Instellen",
      wins: "wint het toernooi, amai!",
      standings: "Stand van zaken",
      pending: "Nog te spelen matchkes",
      nextSwiss: "➡️ Volgende Zwitserse Ronde",
      finish: "🏁 Toernooi Afronden",
      deleteConfirm: "Dit toernooike verwijderen, zijt ge zeker?",
      startTournament: "🏟️ Toernooi Starten",
      bracketReady: "Schema is klaar! Geef hieronder de resultaten in, hé.",
      tournamentComplete: "Toernooike gedaan!",
      noTournament: "Kies deelnemers en start da spel.",
      round: (n: number) => `Ronde ${n}`,
      swissRoundsPlayed: (n: number, total: number) => `${n} van de ${total} rondes gespeeld`,
    },
    multiplier: {
      title: "🎚️ Score Vermenigvuldigers",
      active: (n: number, total: number) => `(${n}/${total} actief, voilà)`,
      description:
        "Zet vermenigvuldigers aan of uit en stel ze in. Wijzigingen gelden voor de volgende matchkes.",
      parameters: "Parameters",
      scale: "Basis",
      scaleDesc: "Basisvermenigvuldiger om de punten schoon te houden.",
      weight: "Activiteitsgewicht",
      weightDesc: "Punten aanpassen volgens belang van de activiteit",
      upset: "Surprise Bonus",
      upsetDesc: "Bonus als ge wint terwijl niemand het verwachtte",
      streak: "Reekske",
      streakDesc: "Bonus voor meerdere overwinningen na elkaar",
      fatigue: "Moeheid",
      fatigueDesc: "Minder opbrengst na veel spelen",
      momentum: "Momentumke",
      momentumDesc: "Bonus voor een goe reekske",
      comeback: "Comebackke",
      comebackDesc: "Bonus als ge terugkomt van ver achter",
      consistency: "Constante",
      consistencyDesc: "Bonus voor stabiele prestaties",
      closeness: "Nipt Matchke",
      closenessDesc: "Bonus voor spannende matchkes",
      variety: "Variatie",
      varietyDesc: "Bonus om eens iets anders te spelen",
      rivalry: "Rivaliteit",
      rivalryDesc: "Bonus als ge iemand verslaat die u eerder klopte",
      recalculate: "🔄 Alles herberekenen",
      recalculateConfirm:
        "Alles opnieuw berekenen met de huidige instellingen? Elke match wordt opnieuw afgespeeld.",
      recalculated: (n: number) => `✅ ${n} matchke${n === 1 ? "" : "s"} herberekend, parfait.`,
      exportConfig: "📤 Configuratie Exporteren",
      importConfig: "📥 Configuratie Importeren",
      resetDefaults: "↩️ Standaard Herstellen",
      invalidFile: "Geen geldig bestand, hé.",
    },
    hint: {
      scaleFactor:
        "Past de basispunten aan die ge krijgt voor matchkes. Zet hoger als ’t te weinig lijkt, lager als ’t te zot wordt, hein.",
      fatigueHalfLife:
        "Na zoveel matchkes wordt uw bonus door moeheid gehalveerd. Laag = rap kapot.",
      momentumFactor: "Bonus per overwinning na mekaar. 0.05 = +5% per winst in uw reekske.",
      momentumCap: "Maximale momentum-bonus. 0.3 = plafondke van +30%, voilà.",
      comebackThreshold:
        "Hoe ver ge moet achterstaan voor een comebackke begint. Lager = sneller actief.",
      comebackMaxBonus: "Maximale comeback-bonus. 0.5 = tot +50% als ge ferm achterstaat.",
      consistencySigmaThreshold: "Onzekerheid (σ) moet hieronder zitten. Lager = strenger, hé.",
      consistencyBonus: "Max bonus voor consistente spelers. 0.15 = tot +15%, netjes.",
      closenessMaxDelta:
        "Max verschil in rating (Δμ) voor een ‘nipt’ matchke. Kleiner = enkel echt spannende duels.",
      closenessBonus: "Max bonus voor nipte matchkes. 0.2 = tot +20%, amai.",
      varietyDecay: "Hoe rap de variatiebonus wegzakt. Lager = vergeeft rapper.",
      varietyBonus: "Max bonus voor iets nieuws te spelen. 0.2 = tot +20%, allez.",
      rivalryLookback:
        "Hoeveel vorige matchkes ge bekijkt voor oude nederlagen. Meer = langer geheugenke.",
      rivalryBonus: "Bonus als ge uw rivaal klopt. 0.25 = tot +25%, schoon hé.",
    },
    data: {
      export: "📤 Exporteerke",
      import: "📥 Importeerke",
      reset: "🗑 Reset alles",
      resetConfirm: "Alles wissen? Ge kunt dat nie meer terugdraaien, pas op!",
      resetScores: "🔄 Scores Resetten",
      resetScoresConfirm:
        "Alle scores, matchkes en pariekes wissen? Spelers en activiteiten blijven staan, hein.",
      importError: "Da bestand is nie goe, jong.",
    },
    history: {
      title: "📜 Recente Resultaatjes",
      deleteConfirm: "Dit resultaat verwijderen, zijt ge zeker?",
      unknownActivity: "Onbekende activiteit, allez",
      unknownTime: "Onbekend momentje",
      pts: "ptn",
      breakdown: "Detail van de punten",
      base: "Basiske",
    },
    tv: {
      title: "🏆 Trip Scorebord-ke",
      noPlayers: "Nog geen spelerskes",
      waitingForPlayers: "Aan ’t wachten op volk...",
      noMatchesYet: "Nog geen matchkes gespeeld",
      matchesPlayed: (n: number) => `${n} matchke${n === 1 ? "" : "s"} gespeeld, voilà`,
    },
    admin: {
      matchmaking: "🎯 Slimme Matchmaking, hein",
      matchmakingTeams: "Teamskes:",
      matchmakingTeamSize: "Grootte van de teams:",
      matchmakingSuggest: "💡 Match Voorstellen",
      suggested: (name: string) => `📋 Voorstelletje: ${name}`,
      balance: "Balanske",
      skill: "Skill, allez vaardigheid",
    },
    arena: {
      railGame: "Activiteitje",
      railPodium: "Podium",
      railRecap: "Overzichtje",
      railLineup: "Opstelling",
      railBracket: "Schema",
      matchCount: (n: number) => `${n} activiteitje${n === 1 ? "" : "s"}`,
      changeResult: "Resultaat aanpassen",
      back: "Terugke",
      recordResults: "Resultaten ingeven →",
      recordResult: "Resultaat ingeven",
      confirmSave: "Bevestigen",
      crownChampion: "Kroon de kampioen",
      finishTournament: "Toernooi afronden",
      stagePickGame: "Stap 1 · Kies een activiteitje",
      whatPlaying: "Wa gaan we doen?",
      needPlayersArena: "Zet eerst minstens 2 spelers in het Organisere-paneel, hé.",
      needActivitiesArena: "Nog geen activiteiten. Voeg er ene toe via Organisere.",
      tapToStart: "Tik op een activiteitje om uw team samen te stellen.",
      formatFfa: "Iedereen voor zich",
      formatTeams: "Teamskes",
      formatTournament: "Toernooike",
      stageLineup: "Stap 2 · Opstelling",
      lineupSubtitle: "Sleep spelers naar de teamskes, of schud alles door mekaar.",
      shuffle: "Schudden",
      bench: "Bankske",
      dropHere: "Hier loslaten",
      dragHere: "Sleep spelers hierheen",
      everyonePlaying: "Iedereen speelt mee, voilà.",
      stagePodium: (n: number) => `Stap ${n} · Podium`,
      whoTookIt: "Wie heeft het gehaald?",
      podiumSubtitle:
        "Tik om te rangschikken. Dubbeltik voor gelijkspel. Tik op een gerangschikte om te annuleren.",
      tied: "(gelijk, hein)",
      recap: "Overzichtje",
      itsATie: "Da's gelijk.",
      weHaveAWinner: "We hebben een winnaar.",
      coChampions: "Co-kampioenen",
      champion: "Kampioen",
      pts: "ptn",
      stageTournament: "Stap 2 · Toernooike",
      standingsPlayer: "Speler",
      standingsMP: "GS",
      standingsW: "W",
      standingsD: "G",
      standingsL: "V",
      standingsPts: "Ptn",
      showLess: "Minder tonen",
      showAll: (n: number) => `Alle ${n} spelers tonen`,
      roundN: (n: number) => `Ronde ${n}`,
      championship: "Finale",
      bracketN: (n: number) => `Schema ${n}`,
      ready: "Klaar",
      bye: "BYE",
      tbd: "Nog te bepalen",
      draw: "Gelijkspel",
      tournamentCompleteMsg: "Toernooike gedaan. Dien het eindklassement in.",
      waitingNextMatch: "Aan 't wachten op het volgende activiteitje…",
      leaveRoom: "Kamer verlansen",
    },
    room: {
      subtitle: "Geef uw kamercode in om mee te doen, hé",
      join: "Binnenkomen",
      placeholder: "bvb. bob",
    },
    general: {
      brandName: "De Vurige Stand",
      vs: "tegen",
      everyone: "Iedereen, tout le monde",
      teamN: (n: number) => `Team ${n}`,
      unassigned: "Nog niet ingedeelde spelers (sleep ze waar ge wilt)",
      dropHere: "Smijt de spelers hier",
      alreadyExists: (name: string) => `"${name}" bestaat al, jong.`,
      required: "Dit veld is verplicht, hé.",
      players: (n: number) => `${n} speler${n === 1 ? "" : "s"}`,
    },
  },
});

export default translations;
